<template>
  <!-- <Picker>: the one way to choose in a form. Pills while everything fits on two lines, otherwise a field
       that says the choice in words with a list behind it (spec §5.4 "Kiezen groeit mee", rules in utils/picker.ts) -->
  <DefinePanel>
    <div class="flex flex-col min-h-0 max-h-[inherit]" @keydown="onKey">
      <div v-if="searchable || quick.length || tabs.length > 1" class="flex flex-col gap-2 px-3 py-2.5 border-b border-ash-2 dark:border-surface-700">
        <!-- A switch of what the list shows (navigation, so the segmented look), never a choice itself -->
        <div v-if="tabs.length > 1" role="tablist" class="inline-flex self-start p-0.5 rounded-full bg-ash dark:bg-surface-800">
          <button v-for="g in tabs" :key="g" type="button" role="tab" :aria-selected="g === shownTab"
            :class="[TAB, g === shownTab ? TAB_ON : TAB_OFF]" @click="tab = g; active = -1">{{ g }}</button>
        </div>
        <input v-if="searchable" ref="searchEl" v-model="query" type="search" :placeholder="t('picker_search')" :aria-label="t('picker_search')"
          role="combobox" aria-autocomplete="list" :aria-controls="listId" aria-expanded="true"
          :aria-activedescendant="active >= 0 ? rowId(active) : undefined"
          class="h-9 px-3 rounded-md border border-[#CFCFCB] dark:border-surface-600 bg-surface-0 dark:bg-surface-950 text-ink dark:text-surface-0 text-sm focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink dark:focus-visible:outline-surface-0" />
        <div v-if="quick.length" class="flex flex-wrap gap-1.5">
          <button v-for="q in quick" :key="q.label" type="button" :aria-pressed="q.on"
            :class="[CHIP, q.on ? CHIP_ON : CHIP_OFF]" @click="q.pick">{{ q.label }}</button>
        </div>
      </div>
      <div :id="listId" ref="listEl" role="listbox" :aria-multiselectable="multiple || undefined" :aria-label="t('picker_list')"
        :tabindex="searchable ? -1 : 0" :aria-activedescendant="!searchable && active >= 0 ? rowId(active) : undefined"
        class="overflow-y-auto min-h-0 flex-1 py-1 focus:outline-none">
        <template v-for="(r, i) in rows" :key="r.key">
          <div v-if="r.type === 'group'" :id="rowId(i)" role="presentation"
            class="flex items-center gap-2.5 px-3.5 pt-2.5 pb-1 text-xs font-bold text-ink-2 dark:text-surface-300 cursor-pointer"
            :class="{ 'bg-ash dark:bg-surface-800': active === i }" @click="r.toggle()" @mousemove="active = i">
            <input v-if="multiple" type="checkbox" tabindex="-1" aria-hidden="true" :checked="r.state === 'all'" :indeterminate="r.state === 'some'"
              class="w-[15px] h-[15px] m-0 accent-ink dark:accent-surface-0 pointer-events-none" />
            <span class="flex-1">{{ r.label }}</span>
            <span v-if="multiple" class="font-medium text-ink-3 dark:text-surface-400 tabular-nums">{{ r.count }}</span>
          </div>
          <div v-else :id="rowId(i)" role="option" :aria-selected="r.on"
            class="grid grid-cols-[18px_minmax(0,1fr)_auto] items-start gap-x-2.5 px-3.5 py-2 text-sm leading-snug cursor-pointer text-ink dark:text-surface-0"
            :class="[active === i ? 'bg-ash dark:bg-surface-800' : 'hover:bg-[#F7F7F5] dark:hover:bg-surface-800', { 'opacity-50 cursor-not-allowed': r.o.disabled }]"
            :aria-disabled="r.o.disabled || undefined" :title="r.o.disabled ? r.o.reason : undefined"
            @click="!r.o.disabled && r.toggle()" @mousemove="active = i">
            <input v-if="multiple" type="checkbox" tabindex="-1" aria-hidden="true" :checked="r.on"
              class="w-[15px] h-[15px] mt-0.5 m-0 accent-ink dark:accent-surface-0 pointer-events-none" />
            <span v-else aria-hidden="true" class="mt-0.5 w-[15px] h-[15px] rounded-full border-[1.5px] border-ink dark:border-surface-0 grid place-items-center">
              <span v-if="r.on" class="w-[7px] h-[7px] rounded-full bg-ink dark:bg-surface-0" />
            </span>
            <span class="flex items-start gap-2 min-w-0">
              <OptionMark :o="r.o" class="mt-[3px]" />
              <span>{{ r.o.label }}</span>
            </span>
            <span v-if="r.o.meta" class="text-xs text-ink-3 dark:text-surface-400 tabular-nums whitespace-nowrap">{{ r.o.meta }}</span>
          </div>
        </template>
        <p v-if="!rows.length" class="m-0 p-3.5 text-ink-3 dark:text-surface-400">{{ t('picker_no_hits', { q: query }) }}</p>
      </div>
      <div v-if="multiple" class="flex items-center justify-between gap-3 px-3 py-2.5 border-t border-ash-2 dark:border-surface-700">
        <span class="text-[13px] text-ink-3 dark:text-surface-400 tabular-nums">{{ t('picker_count', { n: isAllNew ? options.length : chosen.length, of: options.length }) }}</span>
        <button type="button" class="h-8 px-3.5 rounded-full border-0 bg-ink text-white dark:bg-surface-0 dark:text-ink font-semibold cursor-pointer" :class="ring"
          @click="close()">{{ t('done') }}</button>
      </div>
    </div>
  </DefinePanel>

  <!-- Pills -->
  <div v-if="shownMode === 'pills'" v-bind="$attrs" :id="id" role="group" :aria-label="label" :aria-labelledby="labelledby" :aria-describedby="describedby" class="flex flex-wrap gap-1.5">
    <button v-if="allNew" type="button" :aria-pressed="isAllNew" :disabled="disabled" :class="[pill, isAllNew ? pillOn : pillOff]"
      @click="pickAllNew()"><span class="truncate">{{ t('picker_all_new') }}</span></button>
    <button v-for="o in options" :key="String(o.value)" type="button" :aria-pressed="isOn(o.value)" :disabled="disabled || o.disabled"
      :class="[pill, isOn(o.value) ? pillOn : pillOff, { '!px-2.5': o.markOnly }]" :title="o.disabled && o.reason ? o.reason : o.label" @click="toggle(o.value)">
      <OptionMark :o="o" />
      <span :class="o.markOnly ? 'sr-only' : 'truncate'">{{ o.label }}</span>
    </button>
  </div>

  <!-- The field, or in a filter row a pill (§7.4): dashed while empty, solid with the choice in a few words, × to clear -->
  <div v-else v-bind="$attrs" :class="filter ? 'relative inline-flex min-w-0 max-w-full' : 'flex flex-col gap-1.5 min-w-0 w-full'">
    <button :id="id" ref="triggerEl" type="button" :disabled="disabled" aria-haspopup="listbox" :aria-label="label" :aria-labelledby="labelledby" :aria-expanded="open"
      :aria-controls="open ? listId : undefined" :aria-describedby="[describedby, summary && !filter ? summaryId : ''].filter(Boolean).join(' ') || undefined"
      :class="[ring, filter ? filterTrigger : fieldTrigger]"
      @click="toggleOpen">
      <template v-if="filter">
        <span v-if="!isSet" class="truncate text-ink-3 dark:text-surface-400">{{ placeholder ?? t('picker_placeholder') }}</span>
        <template v-else-if="names.shown.length === 1 && !names.more">
          <OptionMark :o="names.shown[0]!" /><span class="truncate" :title="names.shown[0]!.label">{{ names.shown[0]!.label }}</span>
        </template>
        <span v-else class="truncate">{{ summary || (placeholder ?? t('picker_placeholder')) }}</span>
      </template>
      <span v-else-if="isAllNew">{{ t('picker_all_new') }}</span>
      <template v-else-if="names.shown.length">
        <span v-for="o in names.shown" :key="String(o.value)" class="inline-flex items-center gap-1.5 min-w-0 max-w-[13rem]" :title="o.label">
          <OptionMark :o="o" /><span class="truncate">{{ o.label }}</span>
        </span>
        <span v-if="names.more" class="tabular-nums">+{{ names.more }}</span>
      </template>
      <span v-else class="font-normal text-ink-3 dark:text-surface-400">{{ placeholder ?? t('picker_placeholder') }}</span>
      <AppIcon v-if="!(filter && isSet)" icon="chevron-down" class="absolute right-3 top-1/2 -translate-y-1/2 text-ink-3 dark:text-surface-400" />
    </button>
    <button v-if="filter && isSet && !disabled" type="button" :aria-label="t('picker_clear')" :title="t('picker_clear')"
      class="absolute right-1 top-1/2 -translate-y-1/2 grid place-items-center size-6 rounded-full border-0 bg-transparent cursor-pointer text-ink-3 hover:text-ink hover:bg-ash dark:text-surface-400 dark:hover:text-surface-0 dark:hover:bg-surface-800"
      :class="ring" @click="emit('update:modelValue', multiple ? [] : null)"><AppIcon icon="xmark" /></button>
    <small v-if="summary && !filter" :id="summaryId" class="text-[13px] text-ink-3 dark:text-surface-400">{{ summary }}</small>

    <Drawer v-if="sheet" v-model:visible="open" position="bottom" :header="t('picker_list')" class="!h-auto max-h-[85vh]"
      :pt="{ content: { class: '!p-0 flex flex-col min-h-0' } }" @show="focusPanel" @hide="onHide">
      <ReusePanel />
    </Drawer>
    <Popover v-else ref="popover" :pt="{ content: { class: '!p-0' } }" @show="focusPanel" @hide="onHide">
      <div class="flex flex-col max-h-[440px]" :style="{ width: panelWidth }"><ReusePanel /></div>
    </Popover>
  </div>
</template>

<script lang="ts" setup>
import { createReusableTemplate } from '@vueuse/core';
import { defineComponent, h, type Component } from 'vue';
import { pickerMode, pickerSummary, selectedValues, shownNames, FOCUS_RING, CHIP, CHIP_ON, CHIP_OFF, TAB, TAB_ON, TAB_OFF, PILL_ON, filterPill } from '~/utils/picker';

type Option = { value: unknown; label: string; group?: string; mark?: { color?: string; kind?: 'ticket' | 'extra' }; icon?: string; meta?: string;
  /** Shown but not choosable, with the one-line reason as its tip */ disabled?: boolean; reason?: string;
  /** A pill that is only its mark (a colour): the label stays for screen readers and as the tip */ markOnly?: boolean };

// Two template roots (the panel's definition and the visible part), so class and other attributes go to the visible one
defineOptions({ inheritAttrs: false });
const { t } = useI18n();

const props = withDefaults(defineProps<{
  modelValue: unknown;
  options: Option[];
  multiple?: boolean;
  /** Offers "Alle, ook nieuwe": `allValue`, which also covers what is added later, unlike ticking everything. */
  allNew?: boolean;
  allValue?: string | number | null;
  presets?: { label: string; values: unknown[] }[];
  placeholder?: string;
  /** Plural for the summary line: "6 van 14 ticketsoorten"; picker_noun when not given. */
  noun?: string;
  id?: string;
  describedby?: string;
  disabled?: boolean;
  /** A name for the choice when no <Field> label points at it: `label` as text, or `labelledby` an element id */
  label?: string;
  labelledby?: string;
  mode?: 'pills' | 'field';
  /** `filter`: the trigger is a pill for a filter row; always the list, never pills */
  variant?: 'field' | 'filter';
  /** The groups as a switch on top (one group in the list at a time), e.g. Aankomend / Afgelopen */
  groupTabs?: boolean;
}>(), { allValue: null, variant: 'field' });
// `open`: the list opens, e.g. to fetch the counts shown as meta only then
const emit = defineEmits<{ 'update:modelValue': [value: unknown]; open: [] }>();

const [DefinePanel, ReusePanel] = createReusableTemplate();

const AppIconC = resolveComponent('AppIcon');
const TicketMarkC = resolveComponent('TicketMark');
// An option shows its ticket mark (a ticket type or an extra) or its icon
const OptionMark = defineComponent({
  props: { o: { type: Object as () => Option, required: true } },
  setup: (p) => () => p.o.mark
    ? h(TicketMarkC as Component, { color: p.o.mark.color, kind: p.o.mark.kind ?? 'ticket', size: 16 })
    : p.o.icon ? h(AppIconC as Component, { icon: p.o.icon, class: 'shrink-0' }) : null,
});

const ring = FOCUS_RING;
const pill = `inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-[13px] font-semibold max-w-[220px] cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 ${ring}`;
const pillOn = PILL_ON;
const pillOff = 'bg-surface-0 text-ink border-[#CFCFCB] dark:bg-surface-900 dark:text-surface-0 dark:border-surface-600';
const fieldTrigger = computed(() => ['relative flex flex-wrap items-center gap-x-3 gap-y-1 min-h-10 w-full pl-3 pr-10 py-1 text-left rounded-md border bg-surface-0 dark:bg-surface-900 text-ink dark:text-surface-0 text-sm font-medium',
  props.disabled ? 'opacity-60 cursor-not-allowed border-[#CFCFCB] dark:border-surface-700' : 'cursor-pointer border-[#CFCFCB] dark:border-surface-600 hover:border-ink-3']);
// The same borders as main.css gives the other .filter-pill inputs, so the row reads as one
const filterTrigger = computed(() => [...filterPill(isSet.value),
  props.disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-surface-500']);

const uid = useId();
const listId = `${uid}-list`;
const summaryId = `${uid}-summary`;
const rowId = (i: number) => `${uid}-row-${i}`;

const filter = computed(() => props.variant === 'filter');
const shownMode = computed(() => filter.value ? 'field' : props.mode ?? pickerMode(props.options));
const isAllNew = computed(() => !!props.allNew && props.modelValue === props.allValue);
// What the list ticks: everything while "Alle, ook nieuwe" is on
const chosen = computed(() => isAllNew.value ? props.options.map(o => o.value) : selectedValues(props.modelValue, props.allValue));
const isOn = (v: unknown) => !isAllNew.value && chosen.value.includes(v);
const isSet = computed(() => isAllNew.value || chosen.value.length > 0);
const names = computed(() => shownNames(props.modelValue, props.options, { allValue: props.allValue }));
// Nothing chosen already reads in the field itself
const summary = computed(() => {
  if (!props.multiple || !(isAllNew.value || chosen.value.length)) return '';
  const s = pickerSummary(props.modelValue, props.options, { allNew: props.allNew, allValue: props.allValue });
  if (s.kind === 'none') return t('picker_placeholder');
  return t(`picker_summary_${s.kind}`, { noun: props.noun ?? t('picker_noun'), ...s, group: s.kind === 'except' ? s.group.toLowerCase() : '' });
});

function set(values: unknown[]) {
  emit('update:modelValue', props.multiple ? props.options.map(o => o.value).filter(v => values.includes(v)) : values[0] ?? null);
}
function pickAllNew() { emit('update:modelValue', isAllNew.value ? (props.multiple ? [] : null) : props.allValue); }
function toggle(v: unknown) {
  if (props.options.find((o) => o.value === v)?.disabled) return; // also from the keyboard
  if (!props.multiple) { set([v]); if (open.value) close(); return; }
  const cur = isAllNew.value ? props.options.map(o => o.value) : chosen.value;
  // In the pills, a pill picked while "Alle, ook nieuwe" is on starts a choice of its own
  if (isAllNew.value && shownMode.value === 'pills') return set([v]);
  set(cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v]);
}

// The list
const open = ref(false);
const query = ref('');
const active = ref(-1);
const searchable = computed(() => props.options.length >= 8);
const sheet = ref(false);
const panelWidth = ref('auto');
const popover = ref();
const triggerEl = ref<HTMLButtonElement>();
const searchEl = ref<HTMLInputElement>();
const listEl = ref<HTMLElement>();

const sameSet = (a: unknown[], b: unknown[]) => a.length === b.length && a.every(v => b.includes(v));
const quick = computed(() => [
  ...(props.allNew ? [{ label: t('picker_all_new'), on: isAllNew.value, pick: pickAllNew }] : []),
  ...(props.presets ?? []).map(p => ({ label: p.label, on: !isAllNew.value && props.multiple && sameSet(chosen.value, p.values), pick: () => set(p.values) })),
]);

type Row = { type: 'group'; key: string; label: string; state: 'all' | 'some' | 'none'; count: string; toggle: () => void }
  | { type: 'option'; key: string; o: Option; on: boolean; toggle: () => void };
// Group tabs: the groups in order; the tab starts at the chosen option's group (else the first)
const tabs = computed(() => props.groupTabs ? [...new Set(props.options.map(o => o.group).filter(Boolean))] as string[] : []);
const tab = ref<string | null>(null);
const shownTab = computed(() => tab.value && tabs.value.includes(tab.value) ? tab.value
  : props.options.find(o => chosen.value.includes(o.value))?.group ?? tabs.value[0] ?? null);
const rows = computed<Row[]>(() => {
  const q = query.value.trim().toLowerCase();
  // With tabs the list shows one group and no group heading (the switch says it)
  const hits = props.options.filter(o => (!q || o.label.toLowerCase().includes(q)) && (!tabs.value.length || o.group === shownTab.value));
  const groups = [...new Set(hits.map(o => o.group))];
  return groups.flatMap(g => {
    const all = props.options.filter(o => o.group === g).map(o => o.value);
    const n = all.filter(v => chosen.value.includes(v)).length;
    const head: Row[] = g && !tabs.value.length ? [{
      type: 'group', key: `g:${g}`, label: g, state: n === all.length ? 'all' : n ? 'some' : 'none', count: t('picker_group_count', { n, of: all.length }),
      toggle: () => props.multiple && set(n === all.length ? chosen.value.filter(v => !all.includes(v)) : [...chosen.value, ...all.filter(v => !chosen.value.includes(v))]),
    }] : [];
    return [...head, ...hits.filter(o => o.group === g).map((o): Row => ({
      type: 'option', key: `o:${String(o.value)}`, o, on: chosen.value.includes(o.value), toggle: () => toggle(o.value),
    }))];
  });
});
watch(query, () => { active.value = -1; });

function toggleOpen(e: MouseEvent) {
  if (open.value) return close();
  query.value = ''; active.value = -1;
  sheet.value = window.matchMedia('(max-width: 639px)').matches;
  panelWidth.value = `${Math.max(triggerEl.value?.offsetWidth ?? 0, 320)}px`;
  open.value = true;
  emit('open');
  if (!sheet.value) popover.value?.show(e, triggerEl.value);
}
function close() {
  if (sheet.value) open.value = false;
  else popover.value?.hide();
}
function onHide() { open.value = false; triggerEl.value?.focus(); }
function focusPanel() { nextTick(() => (searchEl.value ?? listEl.value)?.focus()); }

function move(step: number) {
  const n = rows.value.length;
  if (!n) return;
  // Group rows only take part when a group can be ticked
  let i = active.value;
  do i = (i + step + n) % n; while (!props.multiple && rows.value[i]?.type === 'group' && i !== active.value);
  active.value = i;
  nextTick(() => document.getElementById(rowId(i))?.scrollIntoView({ block: 'nearest' }));
}
function onKey(e: KeyboardEvent) {
  const onButton = (e.target as HTMLElement).tagName === 'BUTTON';
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); move(e.key === 'ArrowDown' ? 1 : -1); }
  else if (e.key === ' ' && active.value >= 0 && !onButton) { e.preventDefault(); rows.value[active.value]?.toggle(); }
  else if (e.key === 'Enter' && !onButton) {
    e.preventDefault();
    const r = rows.value[active.value];
    if (!props.multiple && r?.type === 'option') r.toggle();
    else close();
  }
  else if (e.key === 'Escape') { e.preventDefault(); close(); }
  // Typing on the list goes to the search
  else if (e.key.length === 1 && searchEl.value && e.target !== searchEl.value && !e.ctrlKey && !e.metaKey) searchEl.value.focus();
}
</script>
